// ============================================
// DESIGN TOKENS - PEDEJA
// Sistema de Automação de Pedidos para Restaurantes
// ============================================

// CORES
export const colors = {
  // Cores Primárias (Gradiente principal da aplicação)
  primary: {
    main: '#318CD5',
    light: '#4FACFE',
    dark: '#2070B0',
    darker: '#185A8C',
    gradient: 'linear-gradient(135deg, #318CD5, #4FACFE)',
    gradientHover: 'linear-gradient(135deg, #2070B0, #318CD5)',
    gradientVertical: 'linear-gradient(180deg, #318CD5, #4FACFE)',
  },

  // Cores Semânticas (Status e Feedback)
  semantic: {
    success: '#28a745',
    successLight: '#d4edda',
    successDark: '#1e7e34',
    error: '#dc3545',
    errorLight: '#f8d7da',
    errorDark: '#bd2130',
    warning: '#ffc107',
    warningLight: '#fff3cd',
    warningDark: '#e0a800',
    info: '#007bff',
    infoLight: '#cce5ff',
    infoDark: '#0056b3',
  },

  // Cores Neutras (Backgrounds, Textos, Bordas)
  neutral: {
    white: '#ffffff',
    background: '#ffffff',
    backgroundAlt: '#f8f9fa',
    surface: '#ffffff',
    surfaceSecondary: '#f7f7f7',
    surfaceHover: '#f0f0f0',
    border: '#dee2e6',
    borderLight: '#e9ecef',
    borderDark: '#ced4da',
    textPrimary: '#212529',
    textSecondary: '#6c757d',
    textMuted: '#adb5bd',
    textDisabled: '#ced4da',
    textInverse: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.5)',
    overlayLight: 'rgba(0, 0, 0, 0.3)',
  },

  // Status de Pedidos (Kanban)
  orderStatus: {
    awaitingPayment: '#6c757d',     // Cinza
    analysis: '#318CD5',             // Azul
    production: '#ffc107',           // Amarelo
    ready: '#28a745',                // Verde
    completed: '#6c757d',            // Cinza
    cancelled: '#dc3545',            // Vermelho
  },

  // Status de WhatsApp
  whatsapp: {
    connected: '#25D366',
    disconnected: '#dc3545',
    pending: '#ffc107',
  },

  // Status de Caixa
  cashier: {
    open: '#28a745',
    closed: '#6c757d',
  },
};

// ESPAÇAMENTOS (Sistema de 4px)
export const spacing = {
  none: '0',
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
};

// TIPOGRAFIA
export const typography = {
  fontFamily: {
    primary: "'Inter', 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif",
    mono: "'Roboto Mono', 'Courier New', monospace",
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
    xxl: '24px',
    xxxl: '32px',
    display: '48px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// RAIOS DE BORDA
export const borderRadius = {
  none: '0',
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
  full: '9999px',
};

// SOMBRAS
export const shadows = {
  none: 'none',
  xs: '0 1px 2px rgba(0, 0, 0, 0.04)',
  sm: '0 1px 3px rgba(0, 0, 0, 0.08)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.15)',
  xxl: '0 25px 50px rgba(0, 0, 0, 0.25)',
  card: '0 0 10px 0 rgba(0, 0, 0, 0.15)',
  modal: '0 0 24px rgba(0, 0, 0, 0.40)',
  dropdown: '0 4px 12px rgba(0, 0, 0, 0.15)',
  inner: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)',
  focus: '0 0 0 3px rgba(49, 140, 213, 0.25)',
  focusError: '0 0 0 3px rgba(220, 53, 69, 0.25)',
};

// TRANSIÇÕES
export const transitions = {
  fastest: '100ms ease',
  fast: '150ms ease',
  normal: '200ms ease',
  slow: '300ms ease',
  slower: '400ms ease',
  // Easings específicos
  easeOut: '200ms ease-out',
  easeIn: '200ms ease-in',
  easeInOut: '200ms ease-in-out',
  spring: '300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
};

// BREAKPOINTS
export const breakpoints = {
  xs: '480px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1440px',
};

// Z-INDEX
export const zIndex = {
  hide: -1,
  base: 0,
  dropdown: 100,
  sticky: 200,
  fixed: 300,
  sidebar: 350,
  modalBackdrop: 400,
  modal: 500,
  popover: 600,
  tooltip: 700,
  toast: 800,
  max: 9999,
};

// TAMANHOS DE COMPONENTES
export const componentSizes = {
  // Botões
  button: {
    sm: { height: '32px', padding: '8px 12px', fontSize: '14px' },
    md: { height: '40px', padding: '10px 16px', fontSize: '14px' },
    lg: { height: '48px', padding: '12px 24px', fontSize: '16px' },
  },
  // Inputs
  input: {
    sm: { height: '32px', padding: '6px 10px', fontSize: '14px' },
    md: { height: '40px', padding: '8px 12px', fontSize: '14px' },
    lg: { height: '48px', padding: '10px 16px', fontSize: '16px' },
  },
  // Sidebar
  sidebar: {
    expanded: '250px',
    collapsed: '80px',
  },
  // Header
  header: {
    height: '64px',
  },
};

// ANIMAÇÕES
export const animations = {
  fadeIn: 'fadeIn 200ms ease-out',
  fadeOut: 'fadeOut 200ms ease-in',
  slideIn: 'slideIn 200ms ease-out',
  slideOut: 'slideOut 200ms ease-in',
  scaleIn: 'scaleIn 200ms ease-out',
  pulse: 'pulse 2s infinite',
  spin: 'spin 1s linear infinite',
};

// Exportar objeto completo do tema
const tokens = {
  colors,
  spacing,
  typography,
  borderRadius,
  shadows,
  transitions,
  breakpoints,
  zIndex,
  componentSizes,
  animations,
};

export default tokens;
